// Синхронизация очереди: двигатель и состояние для интерфейса.
//
// Оффлайн-состояния обязаны быть ВИДНЫ (offline-first.md §9): человек на кухне должен
// понимать, ушла его печать или ждёт связи. Молчащий индикатор превращает очередь
// в «кажется, не сработало» и провоцирует вторую печать той же этикетки.
//
// **Двигатель и состояние разделены, и это несущее.** Раньше был один хук `useSync`,
// смонтированный ТОЛЬКО на экране печати. Списание при этом доступно на дашборде
// и кухонном табло — и там `enqueue()` уведомлял слушателей, которых не было:
// операция ложилась в очередь со статусом `pending`, нулём попыток и без ошибки,
// и висела там, пока кто-нибудь случайно не откроет экран печати.
//
// Менеджер туда не ходит — это экран повара. То есть он списывал продукт, видел
// баннер отмены, а в мониторинге запись оставалась активной. Ровно тот разрыв между
// кухней и учётом, ради закрытия которого Модуль Б существует.
//
// Поэтому владелец подписок теперь ОДИН (`useSyncEngine` в `AppLayout`), а читать
// состояние может кто угодно (`useSyncState`) — второй экземпляр двигателя означал бы
// двойные подписки и флаш веером.
//
// Поверх TanStack Query, а не ручной пары useState+useEffect: чтение очереди
// асинхронное, и своя реализация свелась бы к пересказу того, что библиотека уже
// делает, — с обычным для такого пересказа набором гонок.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { flushQueue, onDelivered } from './syncEngine';
import { useSession } from '@/shared/session';
import {
  failedOps,
  isSubjectRejected,
  onEnqueued,
  queueSize,
  resumeInterrupted,
  retryFailed,
  sendableCount,
} from './syncQueue';
import {
  cabinetQueueSize,
  failedCabinetOps,
  onCabinetEnqueued,
  pendingCabinetOps,
  resumeInterruptedCabinet,
  retryFailedCabinet,
} from './cabinetQueue';
import { flushCabinetQueue } from './cabinetSyncEngine';

/**
 * Числа очереди для индикатора. Кухня (`syncQueue`) и кабинет (`cabinetQueue`) —
 * две независимые очереди с разными движками, но снаружи это один смысл
 * «сколько ждёт отправки», поэтому в счётчике они складываются.
 */
async function readQueueCounts(): Promise<{ queued: number; failed: number }> {
  const [kitchenQueued, kitchenFailed, cabinetQueued, cabinetFailed] = await Promise.all([
    queueSize(),
    failedOps(),
    cabinetQueueSize(),
    failedCabinetOps(),
  ]);
  return {
    queued: kitchenQueued + cabinetQueued,
    failed: kitchenFailed.length + cabinetFailed.length,
  };
}

export const syncKeys = { state: ['sync', 'state'] as const } as const;

export type SyncState = {
  readonly online: boolean;
  readonly queued: number;
  readonly failed: number;
  /**
   * Сервер не признал СУБЪЕКТА: сессия истекла или отозвана (B-030).
   *
   * Отдельно от `failed` и от «нет сети», хотя снаружи все три выглядят как
   * «не уехало». Разница в том, что делать: сеть вернётся сама, ошибку повторяют
   * кнопкой, а здесь не поможет ни то ни другое — нужен вход. Признак вычислялся
   * в `FlushResult.unauthorized` с самого начала и не читался ни одним компонентом.
   */
  readonly needsLogin: boolean;
  readonly syncing: boolean;
  readonly retry: () => void;
};

/** Периодичность фоновой проверки. */
const POLL_MS = 30_000;

/**
 * ДВИГАТЕЛЬ очереди: подписки и отправка. Монтируется РОВНО ОДИН РАЗ — в `AppLayout`.
 *
 * Смонтировать его на экране значит потерять всё, что поставлено в очередь с других
 * экранов. Очередь общая для приложения, и владелец у неё обязан быть общий.
 */
export function useSyncEngine(): SyncState {
  // Чей вход — повод дослать очередь: сессия сменилась, и всё накопленное на этом
  // устройстве теперь есть кому отправить (автор каждой операции — в её теле, Д4).
  const currentUserId = useSession().data?.id ?? null;
  const queryClient = useQueryClient();

  const state = useQuery({
    queryKey: syncKeys.state,
    queryFn: async () => ({
      online: navigator.onLine,
      ...(await readQueueCounts()),
      // Читается СИНХРОННО из памяти: это состояние последнего прохода очереди,
      // а не свойство операций в базе (B-030).
      needsLogin: isSubjectRejected(),
    }),
    // Фоновая проверка: событие `online` в WebView приходит не всегда — Android
    // считает сеть доступной, пока жив интерфейс, даже если роутер уже не отвечает.
    refetchInterval: POLL_MS,
  });

  const flush = useMutation({
    mutationFn: async () => {
      // Две независимые очереди, один повод отправить. Обе уходят под текущей сессией:
      // кухонная несёт автора в каждой операции, кабинетная его не требует (один менеджер).
      const [kitchen, cabinet] = await Promise.all([flushQueue(), flushCabinetQueue()]);
      return { sent: kitchen.sent + cabinet.sent };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: syncKeys.state });
      // Инвалидация СЕРВЕРНОГО состояния — не здесь, а по подписке `onDelivered`
      // ниже: итог своего прохода не покрывает догоняющий, который и доставляет
      // хвост пакета (B-051).
    },
  });

  useEffect(() => {
    // Синхронизация идёт ТОЛЬКО по инициативе клиента: событие `online`, фоновая
    // проверка, ручной retry. Канала «сервер попросил синкнуться» нет — устройство
    // за NAT'ом, и такой канал был бы вторым способом сделать то же самое,
    // только ненадёжным.
    // Что-то ДОЕХАЛО до сервера — значит серверное состояние изменилось.
    //
    // Инвалидация живёт здесь, а не в мутациях экранов: те срабатывают сразу после
    // постановки в очередь, то есть ДО отправки, и перезапрос возвращает старые числа.
    // Менеджер списывал просрочку, видел баннер отмены и неизменившийся счётчик —
    // не потеря данных, но ровно то расхождение экрана с правдой, которое продукт
    // и должен устранять.
    //
    // Сбрасывается ВСЁ серверное, а не выборочные ключи: очередь не знает, какие
    // экраны затронуты её операциями, и знать не должна — список ключей здесь
    // разъехался бы с типами операций при первой же новой.
    const unsubscribeDelivered = onDelivered(() => {
      void queryClient.invalidateQueries();
    });

    const onOnline = (): void => flush.mutate();
    const onOffline = (): void => {
      void queryClient.invalidateQueries({ queryKey: syncKeys.state });
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // Постановка в очередь — тоже повод отправить. Без этой подписки операция ждала бы
    // события `online`, которое при живой сети не наступает вовсе.
    const unsubscribe = onEnqueued(() => flush.mutate());
    const unsubscribeCabinet = onCabinetEnqueued(() => flush.mutate());

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      unsubscribe();
      unsubscribeCabinet();
      unsubscribeDelivered();
    };
  }, [flush, queryClient]);

  useEffect(() => {
    // Сначала ВЕРНУТЬ прерванные операции в работу, потом флашить (B-012).
    //
    // Порядок несущий. `syncing` живёт в IndexedDB и переживает закрытие приложения,
    // а `pendingOps()` такие операции исключает — флаш до сброса статуса прошёл бы
    // мимо них, и они остались бы в счётчике «ждёт отправки» навсегда, без единой
    // попытки отправки и без признака ошибки.
    void Promise.all([resumeInterrupted(), resumeInterruptedCabinet()]).then(() => {
      // Один флаш при монтировании: приложение могло закрыться с непустой очередью,
      // и ждать нового действия, чтобы доставить старое, незачем. Обе очереди уходят
      // под текущим токеном (автор кухонной операции — в её теле), профиля ждать не надо.
      flush.mutate();
    });
    // Намеренно один раз за жизнь компонента: `flush` пересоздаётся каждым рендером,
    // и зависимость от него дала бы флаш на каждый тик.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Кто-то вошёл (или сменился человек на моноблоке) — дошлём очередь под его сессией:
    // всё, что накопилось на устройстве, теперь есть кому отправить.
    if (currentUserId !== null) {
      flush.mutate();
    }
    // Зависимость ТОЛЬКО от субъекта: `flush` пересоздаётся каждым рендером.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Собственный ТАЙМЕР фоновой отправки, а не реакция на обновление состояния (QA-001).
  //
  // Первая версия этой правки висела на `state.dataUpdatedAt`, и это была петля
  // обратной связи: флаш → `invalidateQueries` → перечитывание состояния →
  // `dataUpdatedAt` изменился → эффект → флаш. Без паузы. На проде это дало бы
  // ~28 запросов в секунду навсегда; замерено 282 POST за 10 секунд простоя.
  //
  // Интервал НЕ зависит от того, что делает флаш, поэтому петли не образуется
  // ни при каком исходе отправки. Реф — чтобы таймер ставился один раз: `flush`
  // пересоздаётся каждым рендером, и зависимость от него пересоздавала бы таймер.
  const flushRef = useRef(flush);
  // Синхронизация рефа — в эффекте, а не в теле рендера: запись во время рендера
  // ломает конкурентный рендеринг (React может рендер выбросить и начать заново).
  useEffect(() => {
    flushRef.current = flush;
  });

  useEffect(() => {
    const timer = setInterval(() => {
      // Условие проверяется В МОМЕНТ ТИКА, а не при постановке таймера: за 30 секунд
      // и сеть, и очередь успевают измениться.
      void Promise.all([sendableCount(), pendingCabinetOps()]).then(([kitchen, cabinet]) => {
        if (kitchen + cabinet.length > 0 && navigator.onLine && !flushRef.current.isPending) {
          flushRef.current.mutate();
        }
      });
    }, POLL_MS);

    return () => clearInterval(timer);
    // Ровно один таймер на жизнь двигателя.
  }, []);

  return {
    online: state.data?.online ?? true,
    queued: state.data?.queued ?? 0,
    failed: state.data?.failed ?? 0,
    needsLogin: state.data?.needsLogin ?? false,
    syncing: flush.isPending,
    retry: () => {
      void Promise.all([retryFailed(), retryFailedCabinet()]).then(() => flush.mutate());
    },
  };
}

/**
 * СОСТОЯНИЕ очереди на чтение: сколько ждёт, есть ли ошибки, есть ли связь.
 *
 * Подписок не заводит и ничего не отправляет — только читает тот же кэш запроса,
 * который наполняет двигатель. Поэтому его можно звать из любого числа экранов.
 *
 * `retry` здесь есть: ручной повтор это действие ПОЛЬЗОВАТЕЛЯ, а не фоновая работа,
 * и кнопка обязана работать там, где она нарисована.
 */
export function useSyncState(): SyncState {
  const queryClient = useQueryClient();

  const state = useQuery({
    queryKey: syncKeys.state,
    queryFn: async () => ({
      online: navigator.onLine,
      ...(await readQueueCounts()),
      // Читается СИНХРОННО из памяти: это состояние последнего прохода очереди,
      // а не свойство операций в базе (B-030).
      needsLogin: isSubjectRejected(),
    }),
    refetchInterval: POLL_MS,
  });

  return {
    online: state.data?.online ?? true,
    queued: state.data?.queued ?? 0,
    failed: state.data?.failed ?? 0,
    needsLogin: state.data?.needsLogin ?? false,
    syncing: state.isFetching,
    retry: () => {
      // Повтор ОТПРАВЛЯЕТ, а не только снимает статус ошибки (B-031).
      //
      // Раньше здесь были `retryFailed()` и инвалидация счётчика — и всё. Бейдж
      // ошибки исчезал (операция переставала быть `error`), но никуда не уезжала:
      // ждала постороннего повода. Человек нажимал «повторить» и получал ровно
      // обратное тому, что обещала кнопка.
      //
      // Отдельно от `useSyncEngine.retry`: там та же пара действий, но флаш берётся
      // из мутации двигателя. Здесь двигателя нет — зовём очередь напрямую.
      void Promise.all([retryFailed(), retryFailedCabinet()]).then(([result]) => {
        void queryClient.invalidateQueries({ queryKey: syncKeys.state });
        // Сервер не признал субъекта — повторять нечего до входа, и попытка
        // упёрлась бы в тот же отказ.
        if (!result.needsLogin) {
          void flushQueue().catch(() => undefined);
          void flushCabinetQueue().catch(() => undefined);
        }
      });
    },
  };
}
